<script setup lang="ts">
import { computedAsync } from '@vueuse/core';

import { DealCodeTag } from '@apps/main/new-desktop/modules/dealCode';

import { AppLinkButton } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import type {
  HotelSelectionCardContext,
  HotelSelectionCardSummaryData,
} from '../models';
import { TripIndicator } from '../../../modules/tripIndicator';

interface HotelSelectionCardSummaryProps {
  data: HotelSelectionCardSummaryData;
  context: HotelSelectionCardContext;
}

const props = defineProps<HotelSelectionCardSummaryProps>();

interface HotelSelectionCardSummaryEmits {
  (eventName: 'peek-price'): void;
}

const emit = defineEmits<HotelSelectionCardSummaryEmits>();

const isPeekPriceExperiment = computedAsync(
  () => isExperimentAsync('peek_price_hotel'),
  false
);
</script>

<template>
  <div class="hotel-selection-card-summary">
    <div class="details-section">
      <TripIndicator
        v-if="props.data.recipeIngredientTypes.length > 1"
        :ingredient-types="props.data.recipeIngredientTypes"
        :active-ingredient-index="props.context.ingredientIndex"
      />

      <div v-if="isPeekPriceExperiment" class="more-dates-and-prices">
        <AppLinkButton
          :text="$t('planner.more_dates_and_prices')"
          size="small"
          @click="emit('peek-price')"
        />
      </div>
    </div>
    <div class="price-section">
      <div class="prices">
        <div v-if="props.data.grossPrice" class="gross-price">
          {{ props.data.grossPrice }}
        </div>
        <div class="total-price">{{ props.data.totalPrice }}</div>
      </div>
      <DealCodeTag v-if="props.data.grossPrice" class="campaign-code-tag" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-card-summary {
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: space-between;

  .details-section {
    display: flex;
    gap: var(--spacing-4);
    flex-direction: column;
    align-items: flex-start;

    .more-dates-and-prices {
      display: flex;
      white-space: nowrap;
    }

    .pax {
      @include font-label-s-light;
      color: var(--color-secondary-60);
    }
  }

  .price-section {
    display: flex;
    flex-direction: column;

    .prices {
      align-self: flex-end;
      display: flex;
      align-items: center;
      gap: var(--spacing-8);

      .total-price {
        @include font-label-xl-bold;
        color: var(--color-secondary-40);
      }

      .gross-price {
        @include font-label-l-light;
        color: var(--color-secondary-60);
        text-decoration: line-through;
      }
    }
  }

  .campaign-code-tag {
    margin-top: var(--spacing-4);
  }
}

// NOTE: min-width is the same as the CARD_WIDTH_BREAKPOINT const in HotelSelectionDynamicScroller.vue file, remember to keep it always equal
@container hotelCard (min-width: 690px) {
  .hotel-selection-card-summary {
    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;

    .details-section {
      align-items: flex-end;
      gap: var(--spacing-8);
    }

    .price-section {
      margin-top: var(--spacing-32);
      flex-direction: column-reverse;
      gap: var(--spacing-8);
    }
  }
}
</style>
